import React, { useState, useEffect, useCallback, useRef } from 'react';
import client from '../../api/client';
import { uploadFile } from '../../utils/upload';
import {
  Plus, Edit3, Trash2, Save, Check, ArrowLeft, Send, StickyNote,
  Upload, Image as ImageIcon, ExternalLink
} from 'lucide-react';
import { toast, confirmDialog } from '../../components/Toast/toast';
import { formatDateTime, parseUTC } from '../../utils/date';

// Допустимые сроки жизни заметки (дни); null = бессрочно — как на бэке
const LIFETIME_OPTIONS = [1, 3, 7, 14, 30, 90, 180];

const EMPTY_FORM = { title: '', text: '', image_url: '', lifetime_days: null };

function lifetimeFromNote(note) {
  if (!note.expires_at || !note.created_at) return null;
  const days = Math.round(
    (parseUTC(note.expires_at) - parseUTC(note.created_at)) / 86400000
  );
  return LIFETIME_OPTIONS.includes(days) ? days : null;
}

export default function NotesAdmin() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [publishingId, setPublishingId] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    client.get('/admin/notes')
      .then(res => setNotes(res.data || []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditing('new');
    setForm(EMPTY_FORM);
    setUploadError('');
  };

  const openEdit = (n) => {
    setEditing(n.id);
    setForm({
      title: n.title || '',
      text: n.text || '',
      image_url: n.image_url || '',
      lifetime_days: lifetimeFromNote(n),
    });
    setUploadError('');
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setUploadError('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const url = await uploadFile(file);
      setForm(prev => ({ ...prev, image_url: url }));
    } catch (err) {
      const detail = err.response?.data?.detail;
      setUploadError(typeof detail === 'string' ? detail : 'Не удалось загрузить фото');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setSaved(false);
    try {
      const payload = {
        title: form.title,
        text: form.text,
        image_url: form.image_url || null,
        lifetime_days: form.lifetime_days,
      };
      if (editing === 'new') {
        await client.post('/admin/notes', payload);
      } else {
        await client.put(`/admin/notes/${editing}`, payload);
      }
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        closeForm();
        load();
      }, 800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await confirmDialog('Удалить заметку?', {
      danger: true,
      okLabel: 'Удалить',
      cancelLabel: 'Отмена',
      detail: 'Заметка будет удалена навсегда. Опубликованные посты в каналах останутся.',
      onConfirm: async () => {
        try {
          await client.delete(`/admin/notes/${id}`);
          load();
        } catch (err) {
          toast.error('Ошибка удаления');
          throw err;
        }
      },
    });
  };

  const handlePublish = async (note) => {
    // Повторная публикация создаст дубль поста — спрашиваем подтверждение
    if (note.telegram_url || note.max_url) {
      const ok = await confirmDialog('Заметка уже публиковалась. Отправить повторно?', {
        confirmLabel: 'Опубликовать',
        cancelLabel: 'Отмена',
      });
      if (!ok) return;
    }
    setPublishingId(note.id);
    try {
      const res = await client.post(`/admin/notes/${note.id}/publish`);
      const { telegram_url, max_url } = res.data || {};
      setNotes(prev => prev.map(n =>
        n.id === note.id ? { ...n, telegram_url, max_url } : n
      ));
      toast.success('Заметка опубликована в каналах Telegram и MAX');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Не удалось опубликовать заметку');
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Список — только когда редактор закрыт (форма не уезжает вниз) */}
      {editing === null && (
      <>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-[#64748b]">
          {notes.length} {notes.length === 1 ? 'заметка' : notes.length < 5 ? 'заметки' : 'заметок'}
        </h2>
        <button
          onClick={openAdd}
          className="flex shrink-0 items-center gap-1.5 rounded-xl bg-[#374151] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.97]"
        >
          <Plus className="h-4 w-4" />
          Добавить
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">Загрузка...</div>
      ) : notes.length === 0 ? (
        <div className="rounded-2xl bg-[#f8fafc] py-16 text-center text-sm text-[#64748b]">
          <StickyNote className="mx-auto mb-2 h-10 w-10 text-[#e2e8f0]" />
          <p>Заметок пока нет</p>
          <p className="mt-1 text-xs">Уход и сезонные заметки появятся на главной странице сайта</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
          {notes.map(n => (
            <div key={n.id} className="rounded-xl border border-[#e2e8f0] bg-white p-3 shadow-sm">
              <div className="flex gap-3">
                {n.image_url && (
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#f1f5f9]">
                    <img src={n.image_url} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#0f172a]">{n.title}</div>
                  {n.text && (
                    <p className="mt-0.5 line-clamp-2 text-xs text-[#64748b]">{n.text}</p>
                  )}
                  {n.created_at && (
                    <div className="mt-1 text-[11px] text-[#94a3b8]">{formatDateTime(n.created_at)}</div>
                  )}
                  {n.expires_at && parseUTC(n.expires_at) < new Date() && (
                    <div className="mt-1">
                      <span className="inline-flex items-center rounded-md bg-[#D64545]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#D64545]">
                        Скрыта на сайте
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => openEdit(n)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] hover:text-[#0f172a]">
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(n.id)} className="grid h-9 w-9 place-items-center rounded-lg text-[#64748b] hover:bg-[#D64545]/10 hover:text-[#D64545]">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Опубликованные посты в каналах */}
              {(n.telegram_url || n.max_url) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {n.telegram_url && (
                    <a
                      href={n.telegram_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 rounded-lg bg-[#4A6FA5]/10 px-2.5 py-1.5 text-xs font-medium text-[#4A6FA5] hover:bg-[#4A6FA5]/20"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Читать в Telegram
                    </a>
                  )}
                  {n.max_url && (
                    <a
                      href={n.max_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 rounded-lg bg-[#6B9B74]/10 px-2.5 py-1.5 text-xs font-medium text-[#6B9B74] hover:bg-[#6B9B74]/20"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Читать в MAX
                    </a>
                  )}
                </div>
              )}

              {/* Публикация в каналы */}
              <div className="mt-2 border-t border-[#f1f5f9] pt-2">
                <button
                  onClick={() => handlePublish(n)}
                  disabled={publishingId === n.id}
                  className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-[#374151] py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1f2937] active:scale-[0.98] disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  {publishingId === n.id ? 'Публикуем...' : 'Опубликовать'}
                </button>
                <p className="mt-1 text-center text-[11px] text-[#94a3b8]">
                  Пост уйдёт в каналы Telegram и MAX
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Редактор заметки — отдельной страницей (как «Новый товар») */}
      {editing !== null && (
        <div className="space-y-4">
          <header className="rounded-2xl border border-[#e2e8f0] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <button onClick={closeForm} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#f1f5f9] text-[#64748b] transition active:scale-90">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h2 className="min-w-0 flex-1 truncate text-sm font-bold text-[#0f172a]">
                {editing === 'new' ? 'Новая заметка' : 'Редактирование'}
              </h2>
            </div>
          </header>
          <div>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Заголовок *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Уход за хвойными зимой"
                  className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Текст</label>
                <textarea
                  rows={6}
                  value={form.text}
                  onChange={e => setForm(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Краткий текст заметки"
                  className="w-full resize-none rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Фото</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  {form.image_url ? (
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[#e2e8f0]">
                      <img src={form.image_url} alt="preview" className="h-full w-full object-cover" />
                    </div>
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border-2 border-dashed border-[#e2e8f0] text-[#e2e8f0]">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 py-2 text-xs font-medium text-[#64748b] transition hover:bg-[#f8fafc] active:scale-95 disabled:opacity-50"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {uploading ? 'Загрузка...' : 'Выбрать фото'}
                    </button>
                    {form.image_url && (
                      <button
                        type="button"
                        onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                        className="text-xs text-[#D64545]/70 hover:text-[#D64545]"
                      >
                        Удалить фото
                      </button>
                    )}
                  </div>
                </div>
                {uploadError && (
                  <p className="mt-1 text-xs text-[#D64545]">{uploadError}</p>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-[#64748b]">Срок жизни</label>
                <select
                  value={form.lifetime_days ?? ''}
                  onChange={e => setForm(prev => ({
                    ...prev,
                    lifetime_days: e.target.value === '' ? null : Number(e.target.value),
                  }))}
                  className="w-full max-w-[320px] rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2 text-sm outline-none focus:border-[#6B9B74] focus:bg-white"
                >
                  <option value="">Бессрочно</option>
                  {LIFETIME_OPTIONS.map(d => (
                    <option key={d} value={d}>
                      {d} {d === 1 ? 'день' : d < 5 ? 'дня' : 'дней'}
                    </option>
                  ))}
                </select>
                {form.lifetime_days != null && (
                  <p className="mt-1 text-xs text-[#94a3b8]">
                    Скроется на сайте {(() => {
                      const base = editing !== 'new'
                        ? (parseUTC(notes.find(n => n.id === editing)?.created_at) || new Date())
                        : new Date();
                      return new Date(base.getTime() + form.lifetime_days * 86400000)
                        .toLocaleDateString('ru-RU');
                    })()}
                  </p>
                )}
              </div>
              <div className="mt-5 flex gap-2">
                <button onClick={closeForm} className="flex-1 rounded-xl border border-[#e2e8f0] py-2.5 text-sm font-medium text-[#64748b] hover:bg-[#f8fafc]">
                  Отмена
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || saved || !form.title.trim() || uploading}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-semibold transition active:scale-[0.98] disabled:opacity-50 ${
                    saved
                      ? 'bg-[#6B9B74]/15 text-[#6B9B74]'
                      : 'bg-[#374151] text-white hover:bg-[#1f2937]'
                  }`}
                >
                  {saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                  {saved ? 'Сохранено!' : saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
